// client/src/components/shared/PageSearch.tsx
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { Button } from '../forms/Button';
import { useTypesenseSearch } from '../../hooks/useTypesenseSearch';
import type { SearchHit } from '../../types/search';

export interface PageSearchProps<T = Record<string, unknown>> {
  /** Typesense collection to search */
  collection: string;
  /** Comma-separated Typesense queryBy fields (defaults to collection's getDefaultQueryBy) */
  queryBy?: string;
  /** Typesense filterBy expression (org filter is auto-added upstream) */
  filterBy?: string;
  /** IDs currently loaded in the paginated list — drives narrow vs jump partitioning */
  loadedIds: Set<string>;
  /** Called when the user selects an off-list hit; page decides navigate vs modal */
  onJumpTo: (hit: SearchHit<T>) => void;
  /** Called on every debounced query change with the in-list intersection */
  onNarrow: (query: string, intersectIds: Set<string>) => void;
  /**
   * Optional: the query as typed, on every change, before any index answer. Lets a page match
   * what it can on its own (e.g. a Service ID) without waiting for the index.
   */
  onQueryChange?: (query: string) => void;
  /** The query to start with (e.g. restored from the page URL); later changes are the user's. */
  initialQuery?: string;

  placeholder?: string;
  perPage?: number;
  debounceMs?: number;
  minChars?: number;
  maxJumpResults?: number;
  /** Accessor for the primary display label on jump hits */
  getJumpLabel?: (hit: SearchHit<T>) => string;
  /** Accessor for a secondary line shown beneath the label (e.g. status · customer · address) */
  getJumpSubtitle?: (hit: SearchHit<T>) => string;
  /**
   * Accessor for the identity used to partition hits into in-list vs off-list
   * and to build the narrow intersection set. Defaults to the Typesense doc's
   * `id` field — override when the loaded list's identity is a different
   * business key (e.g. shadow-collection docs whose Typesense `id` never
   * matches the caller's in-memory ID space).
   *
   * Must be referentially stable (module-level fn or useCallback) — it feeds
   * memo/effect deps, and an inline lambda would refire the narrow effect
   * every render.
   */
  getHitId?: (hit: SearchHit<T>) => string;
  /**
   * Matches that ARE on the page's list but hidden by the page's own group or filters. Said
   * beside the box because the dropdown only lists results that are NOT on the list, so a person
   * reading it would conclude the hidden matches do not exist. `onShow` clears what hides them.
   */
  hiddenOpenMatches?: { count: number; onShow: () => void };
  /**
   * An action the page adds under the dropdown rows (ISS-500: "Show all N past tickets"). It is told
   * how many matches the index reports (`found`), how many of this page's top results are off the
   * list, and the query. Return null to add nothing; the dropdown then opens only for real rows. When
   * it returns something, the dropdown opens for it even if every top result is already on the page:
   * the index may hold many more matches than the first page returned.
   */
  offListFooter?: (ctx: { found: number; offListTotal: number; query: string }) => ReactNode;
  /**
   * A line the page adds under the box once the answer for the CURRENT query is in (ISS-566: "No ticket
   * S260926-GA1 found"). It is told every id the page or the index knows (loaded list plus index hits). It
   * is not asked while the answer is outstanding or when the search failed: both would make it judge
   * the wrong thing. Return null to say nothing.
   */
  noMatchNotice?: (ctx: { query: string; knownIds: string[] }) => string | null;
}

function defaultGetJumpLabel<T>(hit: SearchHit<T>): string {
  const doc = hit.document as Record<string, unknown>;
  return (
    (doc.fullName as string) ??
    (doc.name as string) ??
    (doc.companyName as string) ??
    (doc.displayName as string) ??
    (doc.id as string) ??
    'Result'
  );
}

function defaultGetHitId<T>(hit: SearchHit<T>): string {
  return (hit.document as Record<string, unknown>).id as string;
}

export function PageSearch<T = Record<string, unknown>>({
  collection,
  queryBy,
  filterBy,
  loadedIds,
  onJumpTo,
  onNarrow,
  onQueryChange,
  initialQuery = '',
  placeholder = 'Search…',
  perPage = 20,
  debounceMs = 250,
  minChars = 2,
  maxJumpResults = 5,
  getJumpLabel = defaultGetJumpLabel,
  getJumpSubtitle,
  getHitId = defaultGetHitId,
  hiddenOpenMatches,
  offListFooter,
  noMatchNotice,
}: PageSearchProps<T>) {
  const [query, setQuery] = useState(initialQuery);
  /**
   * The off-list dropdown floats OVER the page, so while open it hides the
   * first few in-list results it sits above. Collapsing tucks it into an inline
   * chip on its own line instead of covering the list. Any new keystroke
   * re-opens it — a new query is a new answer worth seeing.
   */
  const [jumpCollapsed, setJumpCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const jumpListId = useId();

  const updateQuery = (next: string) => {
    setQuery(next);
    setJumpCollapsed(false);
  };

  // Raw input goes straight to the hook, which owns the debounce. This used to
  // debounce here as well and hand the already-debounced string down, so every
  // search waited debounceMs + the hook's own 300ms (550ms by default) before a
  // request was even issued.
  const { data, isFetching, error, answeredQuery } = useTypesenseSearch<T>(collection, query, {
    queryBy,
    filterBy,
    perPage,
    minChars,
    debounceMs,
  });

  /**
   * True while the answer for the CURRENT query is still outstanding — the hook
   * is in flight, or nothing has ever come back for this collection.
   *
   * This matters because the hook debounces internally and holds the previous
   * query's results as placeholder data, so `data` lags `query` by design. Until
   * this was checked, the narrow effect below fired the moment the input crossed
   * `minChars`, with `hits` still empty, handing the parent an EMPTY intersection
   * set. The parent dutifully filtered its list down to nothing and rendered its
   * empty state — "No tickets match this view" — under a query that had simply
   * not been answered yet. On a cold Typesense query that false empty was on
   * screen for many seconds; on a warm one it was a flash. Either way it told the
   * user their record did not exist while the search was still running.
   */
  const searchPending =
    query.length >= minChars && !error && (isFetching || data === undefined);

  // Below minChars there is no active search, so drop any hits still cached from
  // a longer previous query rather than leaving a stale "not on this page"
  // dropdown open under an empty input.
  const hits = useMemo<SearchHit<T>[]>(
    () => (query.length < minChars ? [] : data?.hits ?? []),
    [data, query, minChars],
  );

  const { inList, offList, offListTotal } = useMemo(() => {
    const inL: SearchHit<T>[] = [];
    const offL: SearchHit<T>[] = [];
    for (const h of hits) {
      const id = getHitId(h);
      if (loadedIds.has(id)) inL.push(h);
      else offL.push(h);
    }
    return { inList: inL, offList: offL.slice(0, maxJumpResults), offListTotal: offL.length };
  }, [hits, loadedIds, maxJumpResults, getHitId]);

  // Emit narrow updates to the parent.
  const onNarrowRef = useRef(onNarrow);
  onNarrowRef.current = onNarrow;
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  useEffect(() => {
    onQueryChangeRef.current?.(query);
  }, [query]);

  useEffect(() => {
    if (query.length < minChars) {
      onNarrowRef.current('', new Set());
      return;
    }
    // A FAILED search narrows nothing. React Query settles a permanent failure
    // as `isFetching: false` with `data` still undefined, so without this the
    // pending gate above would latch on forever: an endless spinner, a narrow
    // frozen at whatever it last was, and no way to tell a broken search from a
    // slow one. Clearing the narrow shows the full, unfiltered list, which is
    // the truthful answer when we do not know what matches.
    if (error) {
      onNarrowRef.current('', new Set());
      return;
    }
    // Hold the previous narrow until this query is actually answered. On the
    // first search there is no previous narrow, so the parent keeps showing its
    // full, unfiltered list — the honest state while a search is running, and
    // the opposite of claiming nothing matched.
    if (searchPending) return;
    const intersect = new Set(inList.map((h) => getHitId(h)));
    onNarrowRef.current(query, intersect);
  }, [query, minChars, inList, getHitId, searchPending, error]);

  // The page's own action under the rows. Not offered while an answer is outstanding: its numbers
  // would belong to the previous query.
  const footerNode =
    offListFooter && !searchPending && query.length >= minChars
      ? offListFooter({ found: data?.found ?? 0, offListTotal, query })
      : null;
  const noMatchText = useMemo(() => {
    // The answer must be FOR what is typed: the hook debounces and keeps the previous results until the new ones land.
    if (!noMatchNotice || searchPending || error || query.length < minChars || answeredQuery !== query) return null;
    return noMatchNotice({ query, knownIds: Array.from(new Set([...Array.from(loadedIds), ...hits.map((h) => getHitId(h))])) });
  }, [noMatchNotice, searchPending, error, query, minChars, answeredQuery, loadedIds, hits, getHitId]);
  const hasFooter = footerNode !== null && footerNode !== undefined && footerNode !== false;
  const hasDropdown = offList.length > 0 || hasFooter;

  const jumpOpen = hasDropdown && !jumpCollapsed;

  // Clicking anywhere outside the search (e.g. on a result in the list below)
  // collapses the dropdown rather than leaving it parked on top of the page.
  useEffect(() => {
    if (!jumpOpen) return;
    // pointerdown covers mouse, touch and pen in one listener.
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setJumpCollapsed(true);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [jumpOpen]);

  // The TOTAL, not the capped rows shown: "5 results" over a list that really holds 7 reads as
  // "that is all there is".
  const offListLabel =
    offList.length > 0
      ? `${offListTotal} result${offListTotal !== 1 ? 's' : ''} not on this page`
      : 'More matches than shown';

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(e) => {
        // No stopPropagation: an enclosing dialog or drawer that closes on
        // Escape should still see the key.
        if (e.key === 'Escape' && jumpOpen) setJumpCollapsed(true);
      }}
    >
      <div className="relative">
        {/* The magnifier becomes a spinner while the query is outstanding. The
            wait is real and sometimes seconds long on a cold collection; saying
            so beats a static icon over a list that has not changed yet. */}
        {searchPending ? (
          <span
            role="status"
            aria-label="Searching"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin rounded-full border-2 border-slate-200 border-t-[#007ACC] pointer-events-none"
          />
        ) : (
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => updateQuery(e.target.value)}
          placeholder={placeholder}
          aria-label="Search this page"
          // WHITE, not bg-slate-50. Every page that mounts this sits on a
          // `bg-slate-50` ground, so a slate-50 input on a slate-50 page was
          // grey-on-grey — the field read as disabled and its edges disappeared.
          // White + a firmer ring is what the Projects dashboard uses, and it is
          // the one search treatment in the app people said they could see.
          className="w-full pl-10 pr-9 py-2.5 text-sm font-semibold text-slate-900 bg-white
                     ring-1 ring-slate-300 rounded-xl placeholder:text-slate-400
                     focus:ring-2 focus:ring-[#3FA5FF] outline-none transition-all"
        />
        {query && (
          <button
            type="button"
            onClick={() => updateQuery('')}
            aria-label="Clear search"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Say it out loud. A search that failed and a search that matched nothing
          look identical otherwise, and the list below is now deliberately
          UNFILTERED — the user needs to know it is not a result. */}
      {error && query.length >= minChars && (
        <p role="alert" className="mt-1.5 text-xs text-red-700">
          Search is unavailable right now — showing everything instead.
        </p>
      )}

      {noMatchText && (
        <p role="status" className="mt-1.5 text-xs font-semibold text-slate-700">
          {noMatchText}
        </p>
      )}

      {hiddenOpenMatches && hiddenOpenMatches.count > 0 && (
        <p role="status" className="mt-1.5 text-xs font-semibold text-amber-800">
          {hiddenOpenMatches.count} open match{hiddenOpenMatches.count !== 1 ? 'es are' : ' is'} hidden by your current group or filters.{' '}
          <button type="button" onClick={hiddenOpenMatches.onShow} className="underline font-black">
            Show {hiddenOpenMatches.count !== 1 ? 'them' : 'it'}
          </button>
        </p>
      )}

      {hasDropdown && jumpCollapsed && (
        <Button
          type="button"
          variant="brandTint"
          size="xs"
          onClick={() => setJumpCollapsed(false)}
          aria-expanded={false}
          aria-controls={jumpListId}
          className="mt-1.5"
        >
          {offListLabel}
          <ChevronDown className="w-3.5 h-3.5" />
        </Button>
      )}

      {jumpOpen && (
        <div
          id={jumpListId}
          className="absolute left-0 right-0 top-full mt-2 z-30
                     bg-white rounded-2xl ring-1 ring-slate-200 shadow-lg overflow-hidden"
        >
          <div className="flex items-center justify-between gap-2 pl-4 pr-2 py-1.5 border-b border-slate-100">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              {offListLabel}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setJumpCollapsed(true)}
              aria-expanded
              aria-controls={jumpListId}
              aria-label="Hide results not on this page"
            >
              Hide
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
          </div>
          {/* The listbox holds ONLY options. The header above and the page's footer below are not
              options, and a listbox with other children is invalid ARIA. */}
          {offList.length > 0 && (
            <div role="listbox" aria-label="Results not on this page">
              {offList.map((hit, i) => {
                const subtitle = getJumpSubtitle?.(hit);
                return (
                  <button
                    key={i}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => onJumpTo(hit)}
                    className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-slate-900 truncate">{getJumpLabel(hit)}</div>
                      {subtitle && (
                        <div className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</div>
                      )}
                    </div>
                    <span className="text-xs text-[#007ACC] font-bold shrink-0">Jump to →</span>
                  </button>
                );
              })}
            </div>
          )}
          {hasFooter && <div className="border-t border-slate-100 px-4 py-2.5">{footerNode}</div>}
        </div>
      )}
    </div>
  );
}
